import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import {
  CashCompetenceBridge,
  DashboardMonthOption,
  ExecutiveReport,
  ExecutiveReportVisitsSyncStep,
  InsightsOverview,
  MercadoLivreStatus,
  MercadoAdsMonthOption,
  MercadoAdsOverview,
  MlClaimsReputationReport,
  MlClaimsReputationSyncStep,
  MlSellerReputationSnapshot,
  MlListingRevisionApplyRequest,
  MlListingRevisionApplyResponse,
  MlListingsQualityOverview,
  MlListingsSyncStepResponse,
  MpAccountMoneyDiagnostic,
  EmployeeConfig,
  MpAccountMoneySyncResponse,
  MpAccountMoneyView,
  Orchestration,
  PeriodBusinessView,
  PeriodClosure,
  SanityView,
  SyncJobProgress,
  SummaryResponse,
  SyncRun
} from '../models/api.models';

@Injectable({ providedIn: 'root' })
export class ApiService {
  private readonly baseUrl =
    (globalThis as any).__env?.apiUrl ??
    (globalThis.location?.hostname === 'localhost' || globalThis.location?.hostname === '127.0.0.1'
      ? 'http://localhost:8787'
      : '/api');

  constructor(private readonly http: HttpClient) {}

  async me(): Promise<{ username: string }> {
    return firstValueFrom(
      this.http.get<{ username: string }>(`${this.baseUrl}/auth/me`, {
        withCredentials: true
      })
    );
  }

  async getDashboardMonths(): Promise<{ months: DashboardMonthOption[] }> {
    return firstValueFrom(
      this.http.get<{ months: DashboardMonthOption[] }>(`${this.baseUrl}/dashboard/months`, {
        withCredentials: true
      })
    );
  }

  private periodParams(startDate: string, endDate: string): string {
    return `inicio=${encodeURIComponent(startDate)}&fim=${encodeURIComponent(endDate)}`;
  }

  async processPeriod(startDate: string, endDate: string): Promise<void> {
    await firstValueFrom(
      this.http.post(
        `${this.baseUrl}/conciliacoes/processar?${this.periodParams(startDate, endDate)}`,
        {},
        {
          withCredentials: true
        }
      )
    );
  }

  async getSummary(startDate: string, endDate: string): Promise<SummaryResponse> {
    return firstValueFrom(
      this.http.get<SummaryResponse>(`${this.baseUrl}/conciliacoes/resumo?${this.periodParams(startDate, endDate)}`, {
        withCredentials: true
      })
    );
  }

  async closePeriod(startDate: string, endDate: string): Promise<void> {
    await firstValueFrom(
      this.http.post(
        `${this.baseUrl}/conciliacoes/fechar?${this.periodParams(startDate, endDate)}`,
        {},
        {
          withCredentials: true
        }
      )
    );
  }

  async listClosures(): Promise<{ closures: PeriodClosure[] }> {
    return firstValueFrom(
      this.http.get<{ closures: PeriodClosure[] }>(`${this.baseUrl}/conciliacoes/fechamentos`, {
        withCredentials: true
      })
    );
  }

  async getMercadoLivreStatus(): Promise<MercadoLivreStatus> {
    return firstValueFrom(
      this.http.get<MercadoLivreStatus>(`${this.baseUrl}/integrations/ml/status`, {
        withCredentials: true
      })
    );
  }

  async getMercadoLivreAuthorizationUrl(): Promise<{ authorizationUrl: string; redirectUri: string }> {
    return firstValueFrom(
      this.http.get<{ authorizationUrl: string; redirectUri: string }>(`${this.baseUrl}/integrations/ml/connect`, {
        withCredentials: true
      })
    );
  }

  async syncMercadoLivre(startDate: string, endDate: string): Promise<{
    ok: boolean;
    period: { key: string; startDate: string; endDate: string };
    ordersCount: number;
    movementsInserted: number;
    costsInserted: number;
  }> {
    return firstValueFrom(
      this.http.post<{
        ok: boolean;
        period: { key: string; startDate: string; endDate: string };
        ordersCount: number;
        movementsInserted: number;
        costsInserted: number;
      }>(`${this.baseUrl}/integrations/ml/sync?${this.periodParams(startDate, endDate)}`, {}, { withCredentials: true })
    );
  }

  async startMercadoLivreSync(startDate: string, endDate: string): Promise<{ ok: boolean; job: SyncJobProgress }> {
    return firstValueFrom(
      this.http.post<{ ok: boolean; job: SyncJobProgress }>(
        `${this.baseUrl}/integrations/ml/sync/start?${this.periodParams(startDate, endDate)}`,
        {},
        { withCredentials: true }
      )
    );
  }

  async stepMercadoLivreSync(jobId: number): Promise<{ ok: boolean; job: SyncJobProgress }> {
    return firstValueFrom(
      this.http.post<{ ok: boolean; job: SyncJobProgress }>(
        `${this.baseUrl}/integrations/ml/sync/step?jobId=${encodeURIComponent(String(jobId))}`,
        {},
        { withCredentials: true }
      )
    );
  }

  async getMercadoLivreSyncStatus(jobId: number): Promise<{ ok: boolean; job: SyncJobProgress }> {
    return firstValueFrom(
      this.http.get<{ ok: boolean; job: SyncJobProgress }>(
        `${this.baseUrl}/integrations/ml/sync/status?jobId=${encodeURIComponent(String(jobId))}`,
        { withCredentials: true }
      )
    );
  }

  async listSyncRuns(): Promise<{ runs: SyncRun[] }> {
    return firstValueFrom(this.http.get<{ runs: SyncRun[] }>(`${this.baseUrl}/integrations/ml/sync-runs`, { withCredentials: true }));
  }

  async getInsightsOverview(startDate: string, endDate: string): Promise<InsightsOverview> {
    return firstValueFrom(
      this.http.get<InsightsOverview>(`${this.baseUrl}/insights/overview?${this.periodParams(startDate, endDate)}`, {
        withCredentials: true
      })
    );
  }

  async getPeriodBusinessView(startDate: string, endDate: string): Promise<PeriodBusinessView> {
    return firstValueFrom(
      this.http.get<PeriodBusinessView>(`${this.baseUrl}/insights/period?${this.periodParams(startDate, endDate)}`, {
        withCredentials: true
      })
    );
  }

  async getCashCompetenceBridge(startDate: string, endDate: string): Promise<CashCompetenceBridge> {
    return firstValueFrom(
      this.http.get<CashCompetenceBridge>(`${this.baseUrl}/insights/bridge?${this.periodParams(startDate, endDate)}`, {
        withCredentials: true
      })
    );
  }

  async getSanityView(startDate: string, endDate: string): Promise<SanityView> {
    return firstValueFrom(
      this.http.get<SanityView>(`${this.baseUrl}/integrations/ml/sanity?${this.periodParams(startDate, endDate)}`, {
        withCredentials: true
      })
    );
  }

  async getMercadoAdsOverview(startDate: string, endDate: string, siteId = 'MLB'): Promise<MercadoAdsOverview> {
    const siteParam = `&site_id=${encodeURIComponent(siteId)}`;
    return firstValueFrom(
      this.http.get<MercadoAdsOverview>(`${this.baseUrl}/integrations/ml/ads/overview?${this.periodParams(startDate, endDate)}${siteParam}`, {
        withCredentials: true
      })
    );
  }

  async getMercadoAdsMonths(siteId = 'MLB'): Promise<{
    months: MercadoAdsMonthOption[];
    maxRange: { startDate: string; endDate: string };
  }> {
    return firstValueFrom(
      this.http.get<{
        months: MercadoAdsMonthOption[];
        maxRange: { startDate: string; endDate: string };
      }>(`${this.baseUrl}/integrations/ml/ads/months?site_id=${encodeURIComponent(siteId)}`, {
        withCredentials: true
      })
    );
  }

  async syncMercadoAdsOverview(startDate: string, endDate: string, siteId = 'MLB'): Promise<MercadoAdsOverview> {
    const siteParam = `&site_id=${encodeURIComponent(siteId)}`;
    return firstValueFrom(
      this.http.post<MercadoAdsOverview>(
        `${this.baseUrl}/integrations/ml/ads/sync?${this.periodParams(startDate, endDate)}${siteParam}`,
        {},
        { withCredentials: true }
      )
    );
  }

  async getMlListingsQuality(params: {
    limit?: number;
    offset?: number;
    onlyProblems?: boolean;
    issue?: string | null;
    search?: string | null;
  } = {}): Promise<MlListingsQualityOverview> {
    const query = new URLSearchParams();
    query.set('limit', String(params.limit ?? 120));
    query.set('offset', String(params.offset ?? 0));
    query.set('only_problems', String(params.onlyProblems ?? true));
    if (params.issue) query.set('issue', params.issue);
    if (params.search) query.set('search', params.search);
    return firstValueFrom(
      this.http.get<MlListingsQualityOverview>(`${this.baseUrl}/integrations/ml/listings/quality?${query.toString()}`, {
        withCredentials: true
      })
    );
  }

  async syncMlListingsStep(offset: number, limit = 20): Promise<MlListingsSyncStepResponse> {
    return firstValueFrom(
      this.http.post<MlListingsSyncStepResponse>(
        `${this.baseUrl}/integrations/ml/listings/sync/step?offset=${encodeURIComponent(String(offset))}&limit=${encodeURIComponent(String(limit))}`,
        {},
        { withCredentials: true }
      )
    );
  }

  async applyMlListingRevision(itemId: string, payload: MlListingRevisionApplyRequest): Promise<MlListingRevisionApplyResponse> {
    return firstValueFrom(
      this.http.post<MlListingRevisionApplyResponse>(
        `${this.baseUrl}/integrations/ml/listings/${encodeURIComponent(itemId)}/revision/apply`,
        payload,
        { withCredentials: true }
      )
    );
  }

  getMlListingsExportUrl(): string {
    return `${this.baseUrl}/integrations/ml/listings/export.csv`;
  }

  async getExecutiveReport(startDate: string, endDate: string): Promise<ExecutiveReport> {
    return firstValueFrom(
      this.http.get<ExecutiveReport>(`${this.baseUrl}/reports/executive?${this.periodParams(startDate, endDate)}`, {
        withCredentials: true
      })
    );
  }

  async syncExecutiveReport(startDate: string, endDate: string): Promise<ExecutiveReport> {
    return firstValueFrom(
      this.http.post<ExecutiveReport>(
        `${this.baseUrl}/reports/executive/sync?${this.periodParams(startDate, endDate)}`,
        {},
        { withCredentials: true }
      )
    );
  }

  async syncExecutiveReportVisitsStep(
    startDate: string,
    endDate: string,
    offset: number,
    limit = 6
  ): Promise<ExecutiveReportVisitsSyncStep> {
    const params = `${this.periodParams(startDate, endDate)}&offset=${encodeURIComponent(String(offset))}&limit=${encodeURIComponent(String(limit))}`;
    return firstValueFrom(
      this.http.post<ExecutiveReportVisitsSyncStep>(
        `${this.baseUrl}/reports/executive/visits/sync/step?${params}`,
        {},
        { withCredentials: true }
      )
    );
  }

  async getMlClaimsReputationReport(startDate: string, endDate: string): Promise<MlClaimsReputationReport> {
    return firstValueFrom(
      this.http.get<MlClaimsReputationReport>(
        `${this.baseUrl}/integrations/ml/claims/reputation?${this.periodParams(startDate, endDate)}`,
        { withCredentials: true }
      )
    );
  }

  async syncMlClaimsReputationStep(
    startDate: string,
    endDate: string,
    offset: number,
    limit = 3
  ): Promise<MlClaimsReputationSyncStep> {
    const params = `${this.periodParams(startDate, endDate)}&offset=${encodeURIComponent(String(offset))}&limit=${encodeURIComponent(String(limit))}`;
    return firstValueFrom(
      this.http.post<MlClaimsReputationSyncStep>(
        `${this.baseUrl}/integrations/ml/claims/reputation/sync/step?${params}`,
        {},
        { withCredentials: true }
      )
    );
  }

  async syncMlSellerReputation(): Promise<{ snapshot: MlSellerReputationSnapshot | null }> {
    return firstValueFrom(
      this.http.post<{ snapshot: MlSellerReputationSnapshot | null }>(
        `${this.baseUrl}/integrations/ml/seller-reputation/sync`,
        {},
        { withCredentials: true }
      )
    );
  }

  async syncMercadoPagoAccountMoney(startDate: string, endDate: string, force = false): Promise<MpAccountMoneySyncResponse> {
    const forceParam = force ? '&force=true' : '';
    return firstValueFrom(
      this.http.post<MpAccountMoneySyncResponse>(
        `${this.baseUrl}/integrations/mp/account-money/sync?${this.periodParams(startDate, endDate)}${forceParam}`,
        {},
        { withCredentials: true }
      )
    );
  }

  async syncMercadoPagoReleaseReport(startDate: string, endDate: string, force = false): Promise<MpAccountMoneySyncResponse> {
    const forceParam = force ? '&force=true' : '';
    return firstValueFrom(
      this.http.post<MpAccountMoneySyncResponse>(
        `${this.baseUrl}/integrations/mp/release-report/sync?${this.periodParams(startDate, endDate)}${forceParam}`,
        {},
        { withCredentials: true }
      )
    );
  }

  async getMercadoPagoAccountMoney(startDate: string, endDate: string): Promise<MpAccountMoneyView> {
    return firstValueFrom(
      this.http.get<MpAccountMoneyView>(`${this.baseUrl}/integrations/mp/account-money/resumo?${this.periodParams(startDate, endDate)}`, {
        withCredentials: true
      })
    );
  }

  async getMercadoPagoAccountMoneyDiagnostic(startDate: string, endDate: string): Promise<MpAccountMoneyDiagnostic> {
    return firstValueFrom(
      this.http.get<MpAccountMoneyDiagnostic>(
        `${this.baseUrl}/integrations/mp/account-money/diagnostico?${this.periodParams(startDate, endDate)}`,
        { withCredentials: true }
      )
    );
  }

  getExportUrl(startDate: string, endDate: string): string {
    return `${this.baseUrl}/conciliacoes/export.xlsx?${this.periodParams(startDate, endDate)}`;
  }

  async listEmployees(): Promise<{ employees: EmployeeConfig[] }> {
    return firstValueFrom(
      this.http.get<{ employees: EmployeeConfig[] }>(`${this.baseUrl}/settings/employees`, {
        withCredentials: true
      })
    );
  }

  async upsertEmployee(employee: {
    name: string;
    pixKey: string | null;
    commissionRate: number;
    calculationBase: 'LÍQUIDO' | 'BRUTO';
    status: 'ACTIVE' | 'INACTIVE';
  }): Promise<void> {
    await firstValueFrom(
      this.http.put(
        `${this.baseUrl}/settings/employees/${encodeURIComponent(employee.name)}`,
        employee,
        { withCredentials: true }
      )
    );
  }

  async createSyncOrchestration(
    inicio: string,
    fim: string,
    profile: 'financial' | 'historical' = 'financial'
  ): Promise<{ ok: boolean; orchestration: Orchestration }> {
    return firstValueFrom(
      this.http.post<{ ok: boolean; orchestration: Orchestration }>(
        `${this.baseUrl}/sync/orchestrate?${this.periodParams(inicio, fim)}&profile=${encodeURIComponent(profile)}`,
        {},
        { withCredentials: true }
      )
    );
  }

  async stepSyncOrchestration(id: number): Promise<{ ok: boolean; orchestration: Orchestration }> {
    return firstValueFrom(
      this.http.post<{ ok: boolean; orchestration: Orchestration }>(
        `${this.baseUrl}/sync/orchestrate/step?id=${encodeURIComponent(String(id))}`,
        {},
        { withCredentials: true }
      )
    );
  }

  async getSyncOrchestrationStatus(id: number): Promise<{ ok: boolean; orchestration: Orchestration }> {
    return firstValueFrom(
      this.http.get<{ ok: boolean; orchestration: Orchestration }>(
        `${this.baseUrl}/sync/orchestrate/status?id=${encodeURIComponent(String(id))}`,
        { withCredentials: true }
      )
    );
  }
}
